import { useRef, useState, useEffect } from "react";
import { LogIn, LogOut, BookOpenText, Lock, Unlock } from "lucide-react";
import { useNostr } from "./NostrContext";
import { useProfile } from "@/hooks/nostr";
import { getDisplayName, getProfilePicture } from "applesauce-core/helpers";
import { Link } from "wouter";

function LoginModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { login, hasExtension, hasStoredKey, clearStoredKey } = useNostr();
  const [npubInput, setNpubInput] = useState("");
  const [nsecInput, setNsecInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showNsecSetup, setShowNsecSetup] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [open]);

  // Reset form state when modal closes
  useEffect(() => {
    if (!open) {
      setNpubInput("");
      setNsecInput("");
      setPasswordInput("");
      setError(null);
      setShowNsecSetup(false);
    }
  }, [open]);

  const handleExtensionLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await login("extension");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Extension login failed");
    }
    setLoading(false);
  };

  const handleNpubLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!npubInput.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await login("npub", npubInput.trim());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid npub");
    }
    setLoading(false);
  };

  const handlePasswordUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput) return;
    setLoading(true);
    setError(null);
    try {
      await login("password", passwordInput);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to unlock");
    }
    setLoading(false);
  };

  const handleNsecSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nsecInput.trim() || !passwordInput) return;
    setLoading(true);
    setError(null);
    try {
      await login("nsec", nsecInput.trim(), passwordInput);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid nsec or setup failed");
    }
    setLoading(false);
  };

  const handleReset = () => {
    clearStoredKey();
    setPasswordInput("");
    setError(null);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      onClose={onClose}
      className="backdrop:bg-black/50 bg-transparent p-0 max-w-sm w-full m-auto"
    >
      <div className="bg-neutral-800 rounded-lg p-6 border border-neutral-700">
        <h2 className="text-lg font-bold text-neutral-100 mb-4 text-center">
          Login
        </h2>

        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Extension login */}
        {hasExtension && (
          <button
            onClick={handleExtensionLogin}
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded mb-4 transition-colors"
          >
            {loading ? "Connecting..." : "Login with Extension"}
          </button>
        )}

        {/* Password unlock (if stored key exists) */}
        {hasStoredKey && !showNsecSetup && (
          <>
            {hasExtension && (
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-neutral-800 text-neutral-400">or</span>
                </div>
              </div>
            )}
            <form onSubmit={handlePasswordUnlock}>
              <label className="block text-sm text-neutral-400 mb-1">
                Unlock with password
              </label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Enter your password"
                className="w-full bg-neutral-700 border border-neutral-600 rounded px-3 py-2 text-neutral-100 placeholder-neutral-400 mb-3 focus:outline-none focus:border-purple-500"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-1 bg-neutral-700 hover:bg-neutral-600 text-neutral-300 font-medium py-2 px-4 rounded transition-colors"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={loading || !passwordInput}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded transition-colors"
                >
                  {loading ? "Unlocking..." : "Unlock"}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Nsec setup (if no stored key and no extension, or user clicked to show) */}
        {!hasStoredKey && !hasExtension && (
          <form onSubmit={handleNsecSetup}>
            <label className="block text-sm text-neutral-400 mb-1">
              Enter your nsec
            </label>
            <input
              type="password"
              value={nsecInput}
              onChange={(e) => setNsecInput(e.target.value)}
              placeholder="nsec1..."
              className="w-full bg-neutral-700 border border-neutral-600 rounded px-3 py-2 text-neutral-100 placeholder-neutral-400 mb-3 focus:outline-none focus:border-purple-500"
            />
            <label className="block text-sm text-neutral-400 mb-1">
              Choose an encryption password
            </label>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Password to encrypt your key"
              className="w-full bg-neutral-700 border border-neutral-600 rounded px-3 py-2 text-neutral-100 placeholder-neutral-400 mb-3 focus:outline-none focus:border-purple-500"
            />
            <button
              type="submit"
              disabled={loading || !nsecInput.trim() || !passwordInput}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              {loading ? "Setting up..." : "Login with nsec"}
            </button>
            <p className="text-neutral-500 text-xs mt-2 text-center">
              Your key will be encrypted and stored locally.
            </p>
          </form>
        )}

        {/* Nsec setup option when extension exists but user wants nsec */}
        {!hasStoredKey && hasExtension && !showNsecSetup && (
          <>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-neutral-800 text-neutral-400">or</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowNsecSetup(true)}
              className="w-full bg-neutral-700 hover:bg-neutral-600 text-neutral-300 font-medium py-2 px-4 rounded transition-colors"
            >
              Login with nsec
            </button>
          </>
        )}

        {/* Nsec setup form when user clicked the button */}
        {!hasStoredKey && hasExtension && showNsecSetup && (
          <>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-neutral-800 text-neutral-400">or</span>
              </div>
            </div>
            <form onSubmit={handleNsecSetup}>
              <label className="block text-sm text-neutral-400 mb-1">
                Enter your nsec
              </label>
              <input
                type="password"
                value={nsecInput}
                onChange={(e) => setNsecInput(e.target.value)}
                placeholder="nsec1..."
                className="w-full bg-neutral-700 border border-neutral-600 rounded px-3 py-2 text-neutral-100 placeholder-neutral-400 mb-3 focus:outline-none focus:border-purple-500"
              />
              <label className="block text-sm text-neutral-400 mb-1">
                Choose an encryption password
              </label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Password to encrypt your key"
                className="w-full bg-neutral-700 border border-neutral-600 rounded px-3 py-2 text-neutral-100 placeholder-neutral-400 mb-3 focus:outline-none focus:border-purple-500"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowNsecSetup(false)}
                  className="flex-1 bg-neutral-700 hover:bg-neutral-600 text-neutral-300 font-medium py-2 px-4 rounded transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || !nsecInput.trim() || !passwordInput}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded transition-colors"
                >
                  {loading ? "Setting up..." : "Login"}
                </button>
              </div>
              <p className="text-neutral-500 text-xs mt-2 text-center">
                Your key will be encrypted and stored locally.
              </p>
            </form>
          </>
        )}

        {/* Divider before read-only */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-neutral-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-neutral-800 text-neutral-400">or</span>
          </div>
        </div>

        {/* Read-only mode */}
        <form onSubmit={handleNpubLogin}>
          <input
            type="text"
            value={npubInput}
            onChange={(e) => setNpubInput(e.target.value)}
            placeholder="Enter npub or hex pubkey"
            className="w-full bg-neutral-700 border border-neutral-600 rounded px-3 py-2 text-neutral-100 placeholder-neutral-400 mb-3 focus:outline-none focus:border-purple-500"
          />
          <button
            type="submit"
            disabled={loading || !npubInput.trim()}
            className="w-full bg-neutral-600 hover:bg-neutral-500 disabled:opacity-50 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            View as Read-only
          </button>
        </form>

        <p className="text-neutral-500 text-xs mt-4 text-center">
          Read-only mode lets you browse and copy pages, but not publish.
        </p>
      </div>
    </dialog>
  );
}

function UnlockModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { unlockSigner } = useNostr();
  const [passwordInput, setPasswordInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPasswordInput("");
      setError(null);
    }
  }, [open]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput) return;
    setLoading(true);
    setError(null);
    try {
      await unlockSigner(passwordInput);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to unlock");
    }
    setLoading(false);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      onClose={onClose}
      className="backdrop:bg-black/50 bg-transparent p-0 max-w-sm w-full m-auto"
    >
      <div className="bg-neutral-800 rounded-lg p-6 border border-neutral-700">
        <h2 className="text-lg font-bold text-neutral-100 mb-4 text-center">
          Unlock Signer
        </h2>

        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleUnlock}>
          <label className="block text-sm text-neutral-400 mb-1">
            Enter your password
          </label>
          <input
            type="password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            placeholder="Password"
            className="w-full bg-neutral-700 border border-neutral-600 rounded px-3 py-2 text-neutral-100 placeholder-neutral-400 mb-3 focus:outline-none focus:border-purple-500"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || !passwordInput}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            {loading ? "Unlocking..." : "Unlock"}
          </button>
        </form>

        <p className="text-neutral-500 text-xs mt-4 text-center">
          Enter your password to unlock signing.
        </p>
      </div>
    </dialog>
  );
}

function LoggedInProfile({ pubkey }: { pubkey: string }) {
  const { logout, needsUnlock } = useNostr();
  const profile = useProfile(pubkey);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);

  return (
    <>
      <div className="fixed bottom-4 left-4 z-50 bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 flex items-center gap-3">
        <Link
          href="/"
          className="text-neutral-400 hover:text-neutral-200 transition-colors"
          title="Home"
        >
          <BookOpenText className="w-5 h-5" />
        </Link>
        <img
          className="rounded-full w-8 h-8 object-cover"
          src={getProfilePicture(profile, `https://robohash.org/${pubkey}.png`)}
          alt={getDisplayName(profile)}
        />
        <span className="text-sm text-neutral-200">{getDisplayName(profile)}</span>
        {needsUnlock && (
          <button
            onClick={() => setUnlockModalOpen(true)}
            className="text-yellow-400 hover:text-yellow-300 transition-colors"
            title="Unlock signer"
          >
            <Lock className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={logout}
          className="text-neutral-400 hover:text-neutral-200 transition-colors"
          title="Logout"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
      <UnlockModal open={unlockModalOpen} onClose={() => setUnlockModalOpen(false)} />
    </>
  );
}

export function FloatingProfile() {
  const { pubkey } = useNostr();
  const [modalOpen, setModalOpen] = useState(false);

  if (!pubkey) {
    return (
      <>
        <div className="fixed bottom-4 left-4 z-50 bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 flex items-center gap-3">
          <Link
            href="/"
            className="text-neutral-400 hover:text-neutral-200 transition-colors"
            title="Home"
          >
            <BookOpenText className="w-5 h-5" />
          </Link>
          <button
            onClick={() => setModalOpen(true)}
            className="text-neutral-400 hover:text-neutral-200 transition-colors"
            title="Login"
          >
            <LogIn className="w-5 h-5" />
          </button>
        </div>
        <LoginModal open={modalOpen} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return <LoggedInProfile pubkey={pubkey} />;
}
